import React, { useEffect, useMemo, useState } from 'react';
import { Button, Text, TextInput, View } from "../../Components/Themed"
import { Platform, type NativeSyntheticEvent, type TextInputSelectionChangeEventData } from 'react-native';
import type { TextTagProps } from '../../types';
import { SocialComponentApi, SocialComponentHelpers } from '../api';
import TagSelector from './components/TagSelector';
import { Icons } from '../../Components';
import { useColors } from '../../constants/useColors';

type FormattedTextInputProps = {
    onChangeText?: (data:{ text:string, formatted_text:any }) => void,
    submit_title?:string,
    submit_caller?:string,
    submit_loading?:boolean,
    keep_on_submit?:boolean,
    hide_border?:boolean,
    disable_scroll?:boolean,
    icon_submit?:boolean,
    width:number
    placeholder?:string,
    tag_selector_location?:'bottom'|'top',
    onSelectInput?:() => void
    submit_placement?:'inline'|'bottom',
    onFocusPosition?:(position:{ height:number, width:number, x:number, y:number }) => void
    init_data?: { formatted_text: string, text:string, tags:TextTagProps[] },
    onSubmit:(data:{ text:string, formatted_text:any }, tags:TextTagProps[]) => void
}

const FormattedTextInput = ({ width, disable_scroll, submit_title, hide_border, tag_selector_location, icon_submit, keep_on_submit, submit_caller, submit_placement, placeholder, init_data, onSelectInput, onChangeText, onSubmit, onFocusPosition }:FormattedTextInputProps) => {
    const Colors = useColors();
    const [ input_state, setInputState ] = useState<{
        height:number
    }>({
        height: 50
    });
    const [ data, setData ] = useState<{  
        text: string,
        formatted_text:string,
    }>(
        {
            text: '',
            formatted_text: JSON.stringify(''),
        }
    );
    const [ location, setLocation ] = useState({
        start:0,
        end:0
    });
    const [ tag_selector, setTagSelector ] = useState<{
        visible:boolean,
        type:'player',
        working_tag?:TextTagProps
    }>({ visible: false, type: 'player'  })
    const [ valid_tags, setValidTags ] = useState<TextTagProps[]>([]);

    const { text, formatted_text } = data;
    const { height } = input_state;
    const tags = useMemo(() => SocialComponentHelpers.extractTagsFromText(text, location.start), [location.start])
    const active_tag = tags.find(t => t.active);

    const [ action_loading, setActionLoading ] = useState(false);
    const [ working_tag, setWorkingTag ] = useState<TextTagProps | undefined>();

    useEffect(() => {
        if(!submit_caller){ return }
        handleSubmit();
    },[submit_caller])

    useEffect(() => {
        SocialComponentApi.setEnvironment();
        if(init_data){
            setData({ text: init_data.text, formatted_text: init_data.formatted_text });
            setValidTags(init_data.tags)
        }
    },[JSON.stringify(init_data)])

    useEffect(() => {
        if(active_tag){ return setWorkingTag(active_tag) }
        if(working_tag){
            //validateTag(working_tag)
            searchPlayer(working_tag);
            setWorkingTag(undefined)
            return
        }
    },[active_tag]);

    const validateTag = async(tag:TextTagProps) => {
        let new_tags = valid_tags.concat({ ...tag });
        setValidTags(new_tags);
    }

    const searchPlayer = async(tag:TextTagProps) => {
        const player = await SocialComponentApi.searchPlayerByUsername(tag.tag);
        if(player){
            validateTag({ ...tag, local_id: (Math.random()*10000).toFixed(), data: player, tag_type_id:player.player_id });
        } else {
            setTagSelector({
                visible:true,
                working_tag:tag,
                type:'player'
            });
        }        
    }


    const handleSelectionChange = (ev:NativeSyntheticEvent<TextInputSelectionChangeEventData | any>) => {
        if(ev.nativeEvent.selection.start == location.start){ return }
        setLocation({
            start: ev.nativeEvent.selection.start,
            end: ev.nativeEvent.selection.end
        });
    }

    const handleUpdateTag = async(tag:TextTagProps) => {
        setActionLoading(true);
        const new_text = SocialComponentHelpers.replaceText(text, tag.start, tag.end, `@${tag.tag}`);
        validateTag(tag)
        setData({
            ...data,
            text: new_text
        });
        setTagSelector({ visible: false, type: 'player' })
        setTimeout(() => setActionLoading(false), 100)
    }

    const handleSubmit = () => {
        if(!text){ return }
        let new_tags = SocialComponentHelpers.compareTags(valid_tags, tags);
        onSubmit({ text, formatted_text }, new_tags);
        if(keep_on_submit){ return }
        setData({ text: '', formatted_text: JSON.stringify('') });
        setInputState({ ...input_state, height: 50 })
        setValidTags([]);
    }

    const handleChange = (ev:any) => {
        let text:string = JSON.stringify(ev.text)
        if(text.length < 3 && input_state.height > 50){ setInputState({ ...input_state, height: 50 }) }
        setData({
            ...data,
            text: ev.text,
            formatted_text:text
        });
        if(onChangeText){ onChangeText({ text: ev.text, formatted_text: text }) }
    }

    return (
        <View transparent>
            {tag_selector.visible && tag_selector.working_tag && tag_selector_location != 'bottom' ?
            <View transparent style={{ padding:10 }}>
                <TagSelector
                    working_tag={tag_selector.working_tag}
                    type={tag_selector.type}
                    width={width - 20}
                    onFocusPosition={onFocusPosition}
                    visible={tag_selector.visible}
                    onCancel={() => setTagSelector({ visible: false, type: 'player' })}
                    onSelectTag={(tag) => handleUpdateTag(tag)}
                />
            </View>
            :<></>}
            <View transparent style={{ flexDirection:'row', alignItems:'flex-end' }}>
                {onSelectInput ?
                <Button float style={{ padding:15, width:45, marginRight:5, borderRadius:8, justifyContent:'center', alignItems:'center' }} onPress={() => onSelectInput()}>
                    <Icons.MoreIcon size={18} color={Colors.text.h1} />
                </Button>
                :<></>}
                <View type={hide_border?undefined:'input'} style={{ flex:1, flexDirection:'row', marginLeft:4, marginRight:4 }}>
                    <TextInput
                        value={text}
                        editable={!action_loading}
                        multiline
                        loading={action_loading}
                        focus_on_unload
                        transparent
                        scrollEnabled={!disable_scroll}
                        placeholder={placeholder ?? 'Start typing here...'}
                        onFocusPosition={onFocusPosition}
                        style={{ flex:1, height: Platform.OS == 'web' ? height : undefined }}
                        onContentSizeChange={(e) => {
                            if(Platform.OS != 'web'){ return }
                            setInputState({ ...input_state, height: e.nativeEvent.contentSize.height })
                        }}
                        onSelectionChange={handleSelectionChange}
                        onChange={(ev) => handleChange(ev.nativeEvent)}
                    />
                </View>
                {submit_placement == 'inline' ?
                <Button
                    
                    type='success'
                    style={{ opacity:text ? 1: 0.5, marginLeft:5}}
                    disabled={!text}
                    onPress={() => handleSubmit()}
                >
                    {!icon_submit ?
                    <Text theme='h2' color={Colors.text.white}>{submit_title ?? 'Submit'}</Text>
                    :
                    <Icons.SendIcon size={18} color={Colors.text.white} />
                    }
                </Button>
                :<></>}
            </View>
                {tag_selector.visible && tag_selector.working_tag && tag_selector_location == 'bottom' ?
                <View transparent style={{ padding:10 }}>
                <TagSelector
                    working_tag={tag_selector.working_tag}
                    type={tag_selector.type}
                    width={width - 20}
                    onFocusPosition={onFocusPosition}
                    visible={tag_selector.visible}
                    onCancel={() => setTagSelector({ visible: false, type: 'player' })}
                    onSelectTag={(tag) => handleUpdateTag(tag)}
                />
            </View>
                :<></>}
            {submit_placement == 'bottom' ?
            <Button
                title={submit_title ?? 'Submit'}
                type='success'
                style={{ opacity:text ? 1: 0.5, marginTop:5}}
                disabled={!text}
                onPress={() => handleSubmit()}
            />
            :<></>}
        </View>
    )
}

export default FormattedTextInput